/**
 * 全景项目管理：建项目、上传场景、热点、发布；坐标草稿 + 地图选点 + 保存
 * variant=modal 指挥台弹窗；variant=page 管理后台整页嵌入
 */
import React, { useEffect, useState } from 'react';
import { Button, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Upload } from 'antd';
import { message } from '../../utils/antdMessage';
import PanoramaHotspotPlaceModal from './PanoramaHotspotPlaceModal';

function numOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function PanoramaManageModal({
  open,
  busy,
  projects,
  onClose,
  onCreateProject,
  onPatchProject,
  onLoadBundle,
  onCreateScene,
  onUploadEquirect,
  onPatchScene,
  onDeleteScene,
  onCreateHotspot,
  onDeleteHotspot,
  onRefresh,
  onBeginMapPick,
  mapPickFill = null,
  onConsumeMapPickFill,
  /** modal=弹窗（指挥台）；page=嵌入管理后台整页 */
  variant = 'modal',
  /** 管理后台无地图时关闭选点 */
  allowMapPick = true
}) {
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [sceneName, setSceneName] = useState('');
  const [uploadTargetId, setUploadTargetId] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(null);
  const [hsFrom, setHsFrom] = useState(null);
  const [hsTo, setHsTo] = useState(null);
  const [hsYaw, setHsYaw] = useState(0);
  const [hsPitch, setHsPitch] = useState(0);
  const [hsTitle, setHsTitle] = useState('');
  const [placeOpen, setPlaceOpen] = useState(false);
  const [coordDraft, setCoordDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const pendingFillRef = React.useRef(null);

  useEffect(() => {
    if (mapPickFill?.sceneId) {
      pendingFillRef.current = mapPickFill;
    }
  }, [mapPickFill]);

  const refreshBundle = async (id) => {
    if (!id) {
      setBundle(null);
      setCoordDraft({});
      return;
    }
    const data = await onLoadBundle(id);
    setBundle(data);
    const next = {};
    (data.scenes || []).forEach((s) => {
      next[s.id] = {
        lon: s.lon != null ? Number(s.lon) : null,
        lat: s.lat != null ? Number(s.lat) : null
      };
    });
    const fill = pendingFillRef.current;
    if (fill?.sceneId) {
      next[fill.sceneId] = {
        lon: Number(fill.lon),
        lat: Number(fill.lat)
      };
      pendingFillRef.current = null;
      onConsumeMapPickFill?.();
      message.success('已填入坐标，请点「保存」生效');
    }
    setCoordDraft(next);
  };

  useEffect(() => {
    if (!open || !projectId) return;
    refreshBundle(projectId);
  }, [open, projectId]);

  const scenes = bundle?.scenes || [];
  const hotspots = bundle?.hotspots || [];
  const emptyScenes = scenes.filter((s) => !s.ossKey);

  const draftOf = (sceneId) => coordDraft[sceneId] || { lon: null, lat: null };

  const sameCoord = (a, b) => {
    const n = (v) => (v === '' || v == null ? null : Number(v));
    const ax = n(a?.lon);
    const ay = n(a?.lat);
    const bx = n(b?.lon);
    const by = n(b?.lat);
    if (ax == null && bx == null && ay == null && by == null) return true;
    if (ax == null || bx == null || ay == null || by == null) return ax === bx && ay === by;
    return Math.abs(ax - bx) < 1e-9 && Math.abs(ay - by) < 1e-9;
  };

  const isCoordDirty = (scene) => !sameCoord(
    draftOf(scene.id),
    { lon: scene.lon, lat: scene.lat }
  );

  const setDraftField = (sceneId, field, value) => {
    setCoordDraft((prev) => ({
      ...prev,
      [sceneId]: {
        lon: prev[sceneId]?.lon ?? null,
        lat: prev[sceneId]?.lat ?? null,
        [field]: numOrNull(value)
      }
    }));
  };

  const saveCoords = async (scene) => {
    const d = draftOf(scene.id);
    setSavingId(scene.id);
    try {
      await onPatchScene(scene.id, { lon: d.lon, lat: d.lat });
      message.success('坐标已保存');
      await refreshBundle(projectId);
    } catch (e) {
      message.error(e.message || '保存失败');
    } finally {
      setSavingId(null);
    }
  };

  const startMapPick = (scene) => {
    if (!allowMapPick || typeof onBeginMapPick !== 'function') {
      message.warning('请在指挥台叠加工具箱中使用地图选点，或在此手填经纬度');
      return;
    }
    onBeginMapPick({
      sceneId: scene.id,
      sceneName: scene.name,
      projectId
    });
  };

  const body = (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input
            placeholder="新项目名称"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: 200 }}
          />
          <Button
            type="primary"
            loading={busy}
            onClick={async () => {
              if (!title.trim()) {
                message.warning('请填写项目名称');
                return;
              }
              const p = await onCreateProject(title.trim());
              setTitle('');
              if (p?.id) setProjectId(p.id);
            }}
          >
            新建项目
          </Button>
          <Select
            placeholder="选择项目"
            style={{ width: 220 }}
            value={projectId}
            onChange={(v) => setProjectId(v)}
            options={(projects || []).map((p) => ({
              value: p.id,
              label: `${p.title}（${p.status}）`
            }))}
          />
          <Button
            disabled={!projectId}
            onClick={async () => {
              await onPatchProject(projectId, { status: 'published' });
              message.success('已发布');
              onRefresh?.();
              refreshBundle(projectId);
            }}
          >
            发布
          </Button>
          <Button
            disabled={!projectId}
            onClick={async () => {
              await onPatchProject(projectId, { status: 'draft' });
              message.success('已改回草稿');
              onRefresh?.();
              refreshBundle(projectId);
            }}
          >
            撤回草稿
          </Button>
        </Space>
      </div>

      {projectId && (
        <>
          <h4>场景（等距柱状全景图 2:1）</h4>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
            {allowMapPick
              ? '坐标可手填或「地图选点」；选点确认后仅填入下方，需再点「保存」才写入服务器。'
              : '管理后台请手填经纬度后点「保存」。地图选点请在指挥台叠加工具箱中操作。'}
          </div>
          <Space wrap style={{ marginBottom: 8 }}>
            <Input
              placeholder="场景显示名（可选）"
              value={sceneName}
              onChange={(e) => setSceneName(e.target.value)}
              style={{ width: 160 }}
            />
            <Select
              allowClear
              placeholder="补传到空场景（可选）"
              style={{ width: 200 }}
              value={uploadTargetId}
              onChange={setUploadTargetId}
              options={emptyScenes.map((s) => ({
                value: s.id,
                label: `#${s.id} ${s.name}`
              }))}
            />
            <Upload
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              showUploadList={false}
              beforeUpload={async (file) => {
                try {
                  const preferName = sceneName.trim()
                    || (uploadTargetId
                      ? (scenes.find((s) => Number(s.id) === Number(uploadTargetId))?.name || '')
                      : '');
                  let sceneId = uploadTargetId;
                  if (!sceneId) {
                    const scene = await onCreateScene(
                      projectId,
                      preferName || file.name.replace(/\.[^.]+$/, '')
                    );
                    sceneId = scene.id;
                  } else if (preferName) {
                    await onPatchScene?.(sceneId, { name: preferName });
                  }
                  setUploadPercent(0);
                  await onUploadEquirect(projectId, sceneId, file, ({ percent }) => {
                    setUploadPercent(percent);
                  }, { sceneName: preferName || undefined });
                  setSceneName('');
                  setUploadTargetId(null);
                  setUploadPercent(null);
                  await refreshBundle(projectId);
                } catch (e) {
                  setUploadPercent(null);
                  message.error(e.message || '上传失败');
                  await refreshBundle(projectId);
                }
                return false;
              }}
            >
              <Button type="primary" ghost disabled={busy}>
                {uploadPercent != null
                  ? `上传中 ${uploadPercent}%`
                  : (uploadTargetId ? '补传全景到所选场景' : '新建并上传全景')}
              </Button>
            </Upload>
            {emptyScenes.length > 0 && (
              <Popconfirm
                title={`删除 ${emptyScenes.length} 个未上传空场景？`}
                onConfirm={async () => {
                  for (const s of emptyScenes) {
                    await onDeleteScene(s.id);
                  }
                  message.success('已清理空场景');
                  setUploadTargetId(null);
                  refreshBundle(projectId);
                }}
              >
                <Button danger size="small">清理未上传</Button>
              </Popconfirm>
            )}
          </Space>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            scroll={{ x: 900 }}
            dataSource={scenes}
            columns={[
              { title: 'ID', dataIndex: 'id', width: 56 },
              {
                title: '名称',
                dataIndex: 'name',
                width: 140,
                render: (name, r) => (
                  <Input
                    size="small"
                    defaultValue={name}
                    onBlur={async (e) => {
                      const v = e.target.value.trim();
                      if (!v || v === name) return;
                      await onPatchScene(r.id, { name: v });
                      refreshBundle(projectId);
                    }}
                  />
                )
              },
              {
                title: '经度',
                width: 120,
                render: (_, r) => (
                  <InputNumber
                    size="small"
                    style={{ width: 112 }}
                    value={draftOf(r.id).lon}
                    step={0.00001}
                    placeholder="经度"
                    onChange={(v) => setDraftField(r.id, 'lon', v)}
                  />
                )
              },
              {
                title: '纬度',
                width: 120,
                render: (_, r) => (
                  <InputNumber
                    size="small"
                    style={{ width: 112 }}
                    value={draftOf(r.id).lat}
                    step={0.00001}
                    placeholder="纬度"
                    onChange={(v) => setDraftField(r.id, 'lat', v)}
                  />
                )
              },
              {
                title: '坐标',
                width: allowMapPick ? 200 : 100,
                render: (_, r) => (
                  <Space size={4}>
                    {allowMapPick && (
                      <Button size="small" onClick={() => startMapPick(r)}>
                        地图选点
                      </Button>
                    )}
                    <Button
                      size="small"
                      type="primary"
                      disabled={!isCoordDirty(r)}
                      loading={savingId === r.id}
                      onClick={() => saveCoords(r)}
                    >
                      保存
                    </Button>
                  </Space>
                )
              },
              {
                title: '影像',
                width: 72,
                render: (_, r) => (r.ossKey ? '已上传' : '未上传')
              },
              {
                title: '操作',
                width: 130,
                render: (_, r) => (
                  <Space size={0}>
                    {!r.ossKey && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => setUploadTargetId(r.id)}
                      >
                        选为补传
                      </Button>
                    )}
                    <Popconfirm
                      title="删除该场景？"
                      onConfirm={async () => {
                        await onDeleteScene(r.id);
                        if (Number(uploadTargetId) === Number(r.id)) setUploadTargetId(null);
                        refreshBundle(projectId);
                      }}
                    >
                      <Button type="link" danger size="small">删除</Button>
                    </Popconfirm>
                  </Space>
                )
              }
            ]}
          />

          <h4 style={{ marginTop: 16 }}>热点（场景互跳）</h4>
          <div style={{ marginBottom: 8, color: '#64748b', fontSize: 12 }}>
            在全景画面上点击放置热点（类似 720yun）；播放时点热点跳到目标场景。
          </div>
          <Space wrap style={{ marginBottom: 8 }}>
            <Select
              placeholder="所在场景"
              style={{ width: 140 }}
              value={hsFrom}
              onChange={setHsFrom}
              options={scenes.map((s) => ({ value: s.id, label: s.name }))}
            />
            <Select
              placeholder="跳到"
              style={{ width: 140 }}
              value={hsTo}
              onChange={setHsTo}
              options={scenes.map((s) => ({ value: s.id, label: s.name }))}
            />
            <Input
              placeholder="热点文案（如：大堂处）"
              value={hsTitle}
              onChange={(e) => setHsTitle(e.target.value)}
              style={{ width: 160 }}
            />
            <Button
              type="primary"
              onClick={() => {
                if (!hsFrom || !hsTo) {
                  message.warning('请先选择「所在场景」和「跳到」');
                  return;
                }
                if (Number(hsFrom) === Number(hsTo)) {
                  message.warning('起止场景不能相同');
                  return;
                }
                const fromScene = scenes.find((s) => Number(s.id) === Number(hsFrom));
                if (!fromScene?.imageUrl && !fromScene?.ossKey) {
                  message.warning('所在场景尚未上传全景图');
                  return;
                }
                if (!fromScene?.imageUrl) {
                  message.warning('请先保存/刷新项目后再放置（需场景图片地址）');
                  return;
                }
                setPlaceOpen(true);
              }}
            >
              在画面上点击放置
            </Button>
          </Space>
          <details style={{ marginBottom: 8 }}>
            <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: 12 }}>高级：手动填 yaw / pitch</summary>
            <Space wrap style={{ marginTop: 8 }}>
              <InputNumber addonBefore="yaw" value={hsYaw} onChange={setHsYaw} />
              <InputNumber addonBefore="pitch" value={hsPitch} onChange={setHsPitch} />
              <Button
                onClick={async () => {
                  if (!hsFrom || !hsTo) {
                    message.warning('请选择起止场景');
                    return;
                  }
                  await onCreateHotspot(projectId, {
                    fromSceneId: hsFrom,
                    toSceneId: hsTo,
                    yaw: hsYaw,
                    pitch: hsPitch,
                    title: hsTitle
                  });
                  message.success('热点已添加');
                  setHsTitle('');
                  refreshBundle(projectId);
                }}
              >
                按数字添加
              </Button>
            </Space>
          </details>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={hotspots}
            columns={[
              {
                title: '从',
                dataIndex: 'fromSceneId',
                width: 100,
                render: (id) => scenes.find((s) => Number(s.id) === Number(id))?.name || id
              },
              {
                title: '到',
                dataIndex: 'toSceneId',
                width: 100,
                render: (id) => scenes.find((s) => Number(s.id) === Number(id))?.name || id
              },
              { title: 'yaw', dataIndex: 'yaw', width: 70 },
              { title: 'pitch', dataIndex: 'pitch', width: 70 },
              { title: '文案', dataIndex: 'title' },
              {
                title: '',
                width: 70,
                render: (_, r) => (
                  <Button
                    type="link"
                    danger
                    size="small"
                    onClick={async () => {
                      await onDeleteHotspot(r.id);
                      refreshBundle(projectId);
                    }}
                  >
                    删
                  </Button>
                )
              }
            ]}
          />
          <PanoramaHotspotPlaceModal
            open={placeOpen}
            imageUrl={scenes.find((s) => Number(s.id) === Number(hsFrom))?.imageUrl}
            fromSceneName={scenes.find((s) => Number(s.id) === Number(hsFrom))?.name || ''}
            toSceneName={scenes.find((s) => Number(s.id) === Number(hsTo))?.name || ''}
            hotspotTitle={hsTitle || scenes.find((s) => Number(s.id) === Number(hsTo))?.name || ''}
            onCancel={() => setPlaceOpen(false)}
            onConfirm={async ({ yaw, pitch }) => {
              await onCreateHotspot(projectId, {
                fromSceneId: hsFrom,
                toSceneId: hsTo,
                yaw,
                pitch,
                title: hsTitle || scenes.find((s) => Number(s.id) === Number(hsTo))?.name || ''
              });
              message.success('热点已放置');
              setPlaceOpen(false);
              setHsTitle('');
              refreshBundle(projectId);
            }}
          />
        </>
      )}
    </div>
  );

  if (variant === 'page') {
    if (!open) return null;
    return body;
  }

  return (
    <Modal
      open={open}
      title="全景影像管理"
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose={false}
      maskClosable={false}
    >
      {body}
    </Modal>
  );
}
